import * as React from "react"
import {
  Toast,
  ToastActionElement,
  ToastProps as ShadcnToastProps,
} from "@/components/ui/toast"

// Extend the type to include our custom properties
export interface ToastProps extends ShadcnToastProps {
  id?: string;  // Make id property explicit in our type
}

const TOAST_LIMIT = 5
const TOAST_REMOVE_DELAY = 1000000

type ToasterToast = ToastProps & {
  id: string
}

const actionTypes = {
  ADD_TOAST: "ADD_TOAST",
  UPDATE_TOAST: "UPDATE_TOAST",
  DISMISS_TOAST: "DISMISS_TOAST",
  REMOVE_TOAST: "REMOVE_TOAST",
} as const

let count = 0

function genId() {
  count = (count + 1) % Number.MAX_VALUE
  return count.toString()
}

type ActionType = typeof actionTypes

type Action =
  | {
      type: ActionType["ADD_TOAST"]
      toast: ToastProps  // Changed from Omit<ToasterToast, "id"> to ToastProps
    }
  | {
      type: ActionType["UPDATE_TOAST"]
      toast: Partial<ToasterToast> & { id: string }
    }
  | {
      type: ActionType["DISMISS_TOAST"]
      toastId?: string
    }
  | {
      type: ActionType["REMOVE_TOAST"]
      toastId?: string
    }

interface State {
  toasts: ToasterToast[]
}

const toastTimeouts = new Map<string, ReturnType<typeof setTimeout>>()

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case actionTypes.ADD_TOAST:
      return {
        ...state,
        toasts: [
          { ...action.toast, id: genId() },
          ...state.toasts
        ].slice(0, TOAST_LIMIT),
      }

    case actionTypes.UPDATE_TOAST:
      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === action.toast.id ? { ...t, ...action.toast } : t
        ),
      }

    case actionTypes.DISMISS_TOAST: {
      const { toastId } = action

      // Clear toast timeout if it exists
      if (toastId) {
        if (toastTimeouts.has(toastId)) {
          clearTimeout(toastTimeouts.get(toastId))
          toastTimeouts.delete(toastId)
        }
      } else {
        for (const [id, timeout] of toastTimeouts.entries()) {
          clearTimeout(timeout)
          toastTimeouts.delete(id)
        }
      }

      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === toastId || toastId === undefined
            ? {
                ...t,
                open: false,
              }
            : t
        ),
      }
    }
    case actionTypes.REMOVE_TOAST:
      if (action.toastId === undefined) {
        return {
          ...state,
          toasts: [],
        }
      }
      return {
        ...state,
        toasts: state.toasts.filter((t) => t.id !== action.toastId),
      }
    default:
      return state
  }
}

const listeners: Array<(state: State) => void> = []

let memoryState: State = { toasts: [] }

function dispatch(action: Action) {
  memoryState = reducer(memoryState, action)
  listeners.forEach((listener) => {
    listener(memoryState)
  })
}

// Add the clearAllToasts function
export function clearAllToasts() {
  dispatch({ type: actionTypes.REMOVE_TOAST, toastId: undefined })
}

function toast({
  title,
  description,
  action,
  variant,
  duration = 5000,
  open = true,
  onOpenChange,
  className,
  id,
  ...props
}: ToastProps) {
  const toastId = id || genId()

  const update = (props: ToastProps) =>
    dispatch({
      type: actionTypes.UPDATE_TOAST,
      toast: { ...props, id: toastId },
    })

  const dismiss = () =>
    dispatch({ type: actionTypes.DISMISS_TOAST, toastId })

  // Ensure toast is automatically removed after the duration
  if (duration > 0) {
    toastTimeouts.set(
      toastId,
      setTimeout(() => {
        dismiss();
      }, duration)
    );
  }

  dispatch({
    type: actionTypes.ADD_TOAST,
    toast: {
      title,
      description,
      action,
      variant,
      duration,
      open,
      className,
      id: toastId,
      ...props,
      // Define onOpenChange only once, with the custom behavior
      onOpenChange: (open) => {
        // When toast is closed (open=false), dismiss it
        if (!open) dismiss()
        // Call the user's onOpenChange if provided
        onOpenChange?.(open)
      },
    },
  })

  return {
    id: toastId,
    dismiss,
    update,
  }
}

function useToast() {
  const [state, setState] = React.useState<State>(memoryState)

  React.useEffect(() => {
    listeners.push(setState)
    return () => {
      const index = listeners.indexOf(setState)
      if (index > -1) {
        listeners.splice(index, 1)
      }
    }
  }, [state])

  return {
    ...state,
    toast,
    dismiss: (toastId?: string) =>
      dispatch({ type: actionTypes.DISMISS_TOAST, toastId }),
  }
}

// Export the components and types
export { useToast, toast }
export type { ToastActionElement }
// Export the Toast type separately to avoid naming conflicts
export type { ToasterToast as Toast }
